import React from "react";
import Icon from "./AppIcon";

/**
 * Top-level error boundary.
 *
 * The previous version said "Something went wrong. We encountered an unexpected
 * error while processing your request." and offered one button: back to the
 * home page. That is the exact failure the brief calls out — it tells the user
 * nothing, discards whatever they were doing, and gives them no way to recover
 * in place.
 *
 * This version:
 *   - offers "Try again" FIRST, which re-renders the subtree without a full
 *     navigation, so a transient failure doesn't cost the user their context
 *   - keeps "Go back" as a second option rather than the only one
 *   - shows the actual error message (collapsed) so a user can paste something
 *     useful into a support request instead of "it broke"
 *   - is themed, so a crash doesn't also look broken
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showDetail: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    error.__ErrorBoundary = true;
    this.setState({ errorInfo });
    window.__COMPONENT_ERROR__?.(error, errorInfo);
  }

  handleRetry = () => {
    // Re-mount the subtree. A failed fetch or a transient render error usually
    // clears without the user losing where they were.
    this.setState({ hasError: false, error: null, errorInfo: null, showDetail: false });
  };

  render() {
    if (!this.state?.hasError) return this.props?.children;

    const message = this.state.error?.message;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-12 h-12 rounded-lg bg-error-subtle flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={22} className="text-error" />
          </div>

          <h1 className="text-lg font-semibold text-foreground mb-1">
            This page didn&apos;t load
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            Something failed while rendering. Your work hasn&apos;t been lost — trying
            again usually clears it.
          </p>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-medium transition-colors"
            >
              <Icon name="RotateCw" size={15} />
              Try again
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-border text-foreground hover:bg-muted text-sm font-medium transition-colors"
            >
              <Icon name="ArrowLeft" size={15} />
              Go back
            </button>
          </div>

          {message && (
            <div className="mt-6 text-start">
              <button
                onClick={() => this.setState((s) => ({ showDetail: !s.showDetail }))}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                {this.state.showDetail ? "Hide details" : "Show details"}
              </button>
              {this.state.showDetail && (
                <pre className="mt-2 p-3 rounded-md bg-muted border border-border text-[11px] text-muted-foreground whitespace-pre-wrap break-words max-h-40 overflow-auto">
                  {message}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
